import React, { useState, useEffect, useCallback } from 'react';
import Modal from './Modal';

const UserProfile = ({ 
  actor, 
  onClose, 
  bluesky, 
  isLoggedIn, 
  onLoginRequired,
  isModal = true 
}) => {
  const [isFollowing, setIsFollowing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [profileData, setProfileData] = useState(actor);

  const checkFollowStatus = useCallback(async (isMounted) => {
    const response = await bluesky.getFollowStatus(actor.did);
    if (response.success && isMounted) {
      setIsFollowing(response.isFollowing);
    }
  }, [bluesky, actor.did]);

  useEffect(() => {
    let isMounted = true;

    const fetchProfileData = async () => {
      setIsLoading(true);
      try {
        const response = await bluesky.getProfile(actor.did);
        if (isMounted) {
          if (response.success) {
            setProfileData(response.data);
          } else {
            setError('Failed to load profile data');
          }
        }
      } catch (err) {
        if (isMounted) {
          setError('Error loading profile');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchProfileData();
    if (isLoggedIn) {
      checkFollowStatus(isMounted);
    }

    return () => {
      isMounted = false;
    };
  }, [actor.did, isLoggedIn, bluesky, checkFollowStatus]);

  const handleFollow = async () => {
	if (!isLoggedIn) {
	  onLoginRequired();
	  return;
	}

	setIsLoading(true);
	setError('');
	try {
	  const response = isFollowing
		? await bluesky.unfollowUser(actor.did)
		: await bluesky.followUser(actor.did);
	  
	  if (response.success) {
		setIsFollowing(!isFollowing);
	  } else {
		setError('Failed to ' + (isFollowing ? 'unfollow' : 'follow') + ' user');
	  }
	} catch (err) {
	  setError('An error occurred');
	  // Follow action failed - error shown in UI
	} finally {
	  setIsLoading(false);
	}
  };

  const openBlueSkyProfile = () => {
	window.open(`https://bsky.app/profile/${actor.handle}`, '_blank');
  };

  const profileContent = (
	<div className="user-profile">
	  <div className="profile-header">
		{profileData.banner && (
		  <div 
			className="profile-banner"
			style={{ 
			  backgroundImage: `url(${profileData.banner})`,
			  backgroundColor: '#f0f0f0'
			}}
		  />
		)}
		<div className="profile-info">
		  {profileData.avatar && (
			<img 
			  src={profileData.avatar} 
			  alt={`${profileData.displayName || profileData.handle}'s avatar`}
			  className="profile-avatar"
			/>
		  )}
		  <h2>{profileData.displayName}</h2>
		  <p className="handle">@{profileData.handle}</p>
		  
		  <div className="stats">
			<span>{profileData.followersCount || 0} followers</span>
			<span>{profileData.followsCount || 0} following</span>
			<span>{profileData.postsCount || 0} posts</span>
		  </div>
		</div>
	  </div>

	  {error && <div className="error-message">{error}</div>}

	  <div className="profile-actions">
		
		
		<button
		  onClick={openBlueSkyProfile}
		  className="button button-secondary"
		>
		  View Full Profile on Bluesky
		</button>
	  </div>

	  {profileData.description && (
		<div className="profile-bio">
		  <p>{profileData.description}</p>
		</div>
	  )}
	</div>
  );

  return isModal ? (
	<Modal isOpen={true} onClose={onClose}>
	  {profileContent}
	</Modal>
  ) : profileContent;
};

export default UserProfile;